import type { DecorationDef, DecorationRarity } from "../schema/decoration.js";

const N = (de: string, en: string) => ({ de, en });
const deco = (id: string, name: [string, string], model: string, w: number, h: number, morale: number, rarity: DecorationRarity, source: DecorationDef["source"]): DecorationDef => ({ id, name: N(name[0], name[1]), itemId: `itm.${id.replace(".", "_")}`, model, footprint: { w, h }, morale, rarity, source });

/** Placeable decorations; every model ships already (`prop.*`), the garden keeps its 100 SCR purchase (20-base.md R10, D70). */
export const DECORATIONS: readonly DecorationDef[] = [
  deco("deco.garden", ["Gartenkuppel", "Garden Dome"], "prop.deco_garden_dome", 1, 1, 2, "common", ["scrip"]),
  deco("deco.banner", ["Banner", "Banner"], "prop.deco_banner", 1, 1, 1, "common", ["chest", "shop"]),
  deco("deco.floodlight", ["Flutlicht", "Floodlight"], "prop.deco_floodlight", 1, 1, 1, "common", ["chest"]),
  deco("deco.floor_tile", ["Bodenplatte", "Floor Tile"], "prop.deco_floor_tile", 1, 1, 1, "common", ["chest"]),
  deco("deco.holo_sign", ["Holo-Schild", "Holo Sign"], "prop.deco_holo_sign", 1, 1, 2, "rare", ["chest", "shop"]),
  deco("deco.statue", ["Statue", "Statue"], "prop.deco_statue", 2, 2, 3, "rare", ["event", "shop"]),
  deco("deco.elm", ["Ulme", "Elm"], "prop.grove_elm", 1, 1, 1, "common", ["chest"]),
  deco("deco.yard_water", ["Wasserhof", "Water Yard"], "prop.yard_water", 2, 2, 2, "rare", ["chest", "event"]),
  deco("deco.yard_supply", ["Versorgungshof", "Supply Yard"], "prop.yard_supply", 2, 2, 2, "rare", ["chest", "event"]),
  deco("deco.yard_service", ["Servicehof", "Service Yard"], "prop.yard_service", 2, 2, 3, "epic", ["chest", "event", "shop"]),
];

const byId = new Map(DECORATIONS.map((d) => [d.id, d]));
const byItem = new Map(DECORATIONS.map((d) => [d.itemId, d]));

export function decorationById(id: string): DecorationDef {
  const d = byId.get(id);
  if (!d) throw new Error(`unknown decoration ${id}`);
  return d;
}

export function decorationByItem(itemId: string): DecorationDef | undefined {
  return byItem.get(itemId);
}

/** Placed decorations per base: 5 + HQ level, at most 30. */
export function DECORATION_LIMIT(hqLevel: number): number {
  return Math.min(30, 5 + Math.max(0, hqLevel));
}

/** Bonus roll on equipment chests: chance and the rarities the drop is drawn from (on top of the module, never instead). */
export const DECORATION_CHEST_ODDS: Record<"itm.chest_bronze" | "itm.chest_silver" | "itm.chest_gold", { pct: number; pool: DecorationRarity[] }> = {
  "itm.chest_bronze": { pct: 5, pool: ["common"] },
  "itm.chest_silver": { pct: 10, pool: ["common", "rare"] },
  "itm.chest_gold": { pct: 25, pool: ["rare", "epic"] },
};

/** Decorations that can drop from chests of the given rarities, deterministic order. */
export function decorationPool(rarities: readonly DecorationRarity[]): DecorationDef[] {
  return DECORATIONS.filter((d) => d.source.includes("chest") && rarities.includes(d.rarity));
}
